import { z } from 'zod';
import { CLOSED_BEHAVIOURS, CONDITION_OPERATORS, FEATURE_FLAGS, FIELD_TYPES, FIELD_VISIBILITIES, FLOW_CONDITION_FIELDS, PROVIDER_KINDS, ROLES, VISITOR_STATUSES, } from './types.js';
/**
 * Wire-format schemas.
 *
 * Every request body the API accepts is parsed through one of these. Parsing
 * (not just validating) is intentional: the parsed output is what reaches the
 * business logic, so unknown properties are stripped instead of travelling into
 * a JSONB column.
 */
/* ------------------------------------------------------------------ scalars */
export const idSchema = z.string().uuid();
export const slugSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, 'must be lowercase alphanumeric with - or _');
export const machineNameSchema = z
    .string()
    .min(1)
    .max(48)
    .regex(/^[a-z][a-z0-9_]*$/, 'must start with a letter and contain only a-z, 0-9 and _');
export const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb color');
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM');
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export const languageSchema = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/);
export const shortText = z.string().trim().max(200);
export const longText = z.string().trim().max(2000);
/* -------------------------------------------------------------------- forms */
export const fieldOptionSchema = z.object({
    value: z.string().trim().min(1).max(128),
    label: z.string().trim().min(1).max(200),
    labelI18n: z.record(languageSchema, z.string().max(200)).optional(),
    active: z.boolean().default(true),
    order: z.number().int().min(0).max(9999).default(0),
    icon: z
        .string()
        .regex(/^[a-z][a-z0-9-]{0,31}$/)
        .optional(),
});
export const fieldValidationSchema = z
    .object({
    minLength: z.number().int().min(0).max(2000).optional(),
    maxLength: z.number().int().min(1).max(2000).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().max(200).optional(),
    minDate: z.string().max(20).optional(),
    maxDate: z.string().max(20).optional(),
})
    .strict();
export const formFieldSchema = z.object({
    id: z.string().min(1).max(64),
    name: machineNameSchema,
    type: z.enum(FIELD_TYPES),
    label: z.string().trim().min(1).max(200),
    labelI18n: z.record(languageSchema, z.string().max(200)).optional(),
    placeholder: shortText.optional(),
    placeholderI18n: z.record(languageSchema, z.string().max(200)).optional(),
    helpText: longText.optional(),
    helpTextI18n: z.record(languageSchema, z.string().max(500)).optional(),
    required: z.boolean().default(false),
    visibility: z.enum(FIELD_VISIBILITIES).default('visible'),
    order: z.number().int().min(0).max(9999),
    options: z.array(fieldOptionSchema).max(500).optional(),
    listKey: slugSchema.optional(),
    linkUrl: z
        .string()
        .trim()
        .url()
        .max(500)
        .refine((url) => /^https?:\/\//i.test(url), 'must be an http(s) address')
        .optional(),
    validation: fieldValidationSchema.optional(),
    showWhen: z
        .object({
        field: z.union([machineNameSchema, z.enum(FLOW_CONDITION_FIELDS)]),
        equals: z.array(z.string().max(128)).min(1).max(50),
    })
        .optional(),
    pii: z.boolean().default(false),
    showInNotification: z.boolean().default(true),
});
export const formDefinitionUpsertSchema = z.object({
    visitorTypeKey: slugSchema,
    locationId: idSchema.nullable().default(null),
    title: shortText.optional(),
    fields: z.array(formFieldSchema).max(60),
});
export const optionListUpsertSchema = z.object({
    key: slugSchema,
    name: z.string().trim().min(1).max(120),
    items: z.array(fieldOptionSchema).max(2000),
});
/* ------------------------------------------------------------------ routing */
export const routingConditionSchema = z.object({
    field: z.string().min(1).max(80),
    op: z.enum(CONDITION_OPERATORS),
    value: z
        .union([z.string().max(200), z.number(), z.boolean(), z.array(z.string().max(200)).max(100)])
        .optional(),
});
/** Mirrors the CHECK constraint on notification_groups.key (migration 0005). */
export const notificationGroupKeySchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, 'invalid_group_key');
export const routingTargetsSchema = z.object({
    /** Keys of notification groups; a rule never names an address directly. */
    groups: z.array(notificationGroupKeySchema).max(20).default([]),
    providers: z.array(z.enum(PROVIDER_KINDS)).max(6).default([]),
});
export const notificationGroupUpsertSchema = z.object({
    key: notificationGroupKeySchema,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().default(null),
    webhookKeys: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
    externalEmails: z.array(z.string().trim().toLowerCase().email().max(254)).max(50).default([]),
    memberIds: z.array(z.string().uuid()).max(500).default([]),
});
/**
 * What an employee may change about themselves. Every field is optional so the
 * UI can flip a single switch without resending the whole object.
 */
export const notificationPreferencesSchema = z.object({
    enabled: z.boolean().optional(),
    push: z.boolean().optional(),
    email: z.boolean().optional(),
    /** ISO timestamp, or null to lift the mute. */
    mutedUntil: z.string().datetime({ offset: true }).nullable().optional(),
});
export const routingRuleUpsertSchema = z.object({
    name: z.string().trim().min(1).max(120),
    enabled: z.boolean().default(true),
    priority: z.number().int().min(0).max(9999).default(100),
    match: z.enum(['all', 'any']).default('all'),
    conditions: z.array(routingConditionSchema).max(20).default([]),
    targets: routingTargetsSchema,
    stopOnMatch: z.boolean().default(true),
});
/* ------------------------------------------------------------ opening hours */
export const openingIntervalSchema = z.object({ from: timeSchema, to: timeSchema });
export const openingHoursSchema = z.object({
    timezone: z.string().min(1).max(64),
    days: z.record(z.enum(['1', '2', '3', '4', '5', '6', '7']), z.array(openingIntervalSchema).max(6)),
    holidays: z.array(isoDateSchema).max(400).default([]),
    exceptions: z.record(isoDateSchema, z.array(openingIntervalSchema).max(6)).optional(),
});
/* ----------------------------------------------------------------- branding */
export const brandingSchema = z.object({
    logoUrl: z.string().max(500).nullable().default(null),
    primaryColor: colorSchema.default('#1d4ed8'),
    secondaryColor: colorSchema.default('#0f172a'),
    backgroundColor: colorSchema.default('#f6f8fb'),
    welcomeTitle: z.string().trim().max(120).default('Willkommen'),
    // Per language overrides. Absent or empty means "use the base text", which is
    // why the kiosk can fall back without knowing which languages exist.
    welcomeTitleI18n: z.record(languageSchema, z.string().trim().max(120)).optional(),
    welcomeSubtitle: z.string().trim().max(200).default(''),
    welcomeSubtitleI18n: z.record(languageSchema, z.string().trim().max(200)).optional(),
    footerText: z.string().trim().max(200).nullable().default(null),
    showPoweredBy: z.boolean().default(true),
    poweredByText: z.string().trim().max(80).default('Powered by EC IT-Service'),
    imprintUrl: z.string().max(500).nullable().default(null),
    privacyUrl: z.string().max(500).nullable().default(null),
    privacyNotice: z.string().trim().max(4000).nullable().default(null),
});
/* --------------------------------------------------------------- check-ins */
export const answerValueSchema = z.union([
    z.string().max(2000),
    z.number().finite(),
    z.boolean(),
    z.array(z.string().max(200)).max(50),
    z.null(),
]);
export const checkInSchema = z.object({
    idempotencyKey: z.string().uuid(),
    visitorTypeKey: slugSchema,
    formVersion: z.number().int().min(0).max(100000).nullable().default(null),
    reason: z.string().trim().max(128).nullable().default(null),
    contactTarget: z.string().trim().max(128).nullable().default(null),
    appointment: z
        .object({
        hasAppointment: z.boolean(),
        withPerson: z.string().trim().max(200).nullable().default(null),
        reference: z.string().trim().max(64).nullable().default(null),
        company: z.string().trim().max(200).nullable().default(null),
    })
        .nullable()
        .default(null),
    answers: z.record(machineNameSchema, answerValueSchema).default({}),
    language: languageSchema.default('de'),
    clientCreatedAt: z.string().datetime({ offset: true }),
    consentAccepted: z.boolean().default(false),
});
/** A device may flush several offline submissions in one request. */
export const checkInBatchSchema = z.object({
    submissions: z.array(checkInSchema).min(1).max(50),
});
export const visitorStatusUpdateSchema = z.object({
    status: z.enum(VISITOR_STATUSES),
    note: z.string().trim().max(500).optional(),
});
/* ----------------------------------------------------------------- devices */
export const devicePairSchema = z.object({
    pairingCode: z.string().trim().min(4).max(20),
    appVersion: z.string().max(32).default('unknown'),
    userAgent: z.string().max(400).optional(),
    screen: z.string().max(32).optional(),
    os: z.string().max(64).optional(),
});
export const deviceHeartbeatSchema = z.object({
    appVersion: z.string().max(32),
    userAgent: z.string().max(400).optional(),
    screen: z.string().max(32).optional(),
    os: z.string().max(64).optional(),
    queuedSubmissions: z.number().int().min(0).max(10000).optional(),
});
export const deviceUpsertSchema = z.object({
    name: z.string().trim().min(1).max(120),
    locationId: idSchema,
    notes: z.string().trim().max(500).optional(),
});
/* -------------------------------------------------------------------- auth */
export const loginSchema = z.object({
    email: z.string().trim().email().max(254),
    password: z.string().min(1).max(256),
    totp: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
export const passwordSchema = z
    .string()
    .min(12, 'at least 12 characters')
    .max(256)
    .refine((v) => /[a-z]/.test(v), 'must contain a lowercase letter')
    .refine((v) => /[A-Z]/.test(v), 'must contain an uppercase letter')
    .refine((v) => /\d/.test(v), 'must contain a digit');
export const userCreateSchema = z.object({
    email: z.string().trim().email().max(254),
    name: z.string().trim().min(1).max(120),
    role: z.enum(ROLES),
    password: passwordSchema,
    locationIds: z.array(idSchema).max(200).default([]),
});
export const userUpdateSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    role: z.enum(ROLES).optional(),
    active: z.boolean().optional(),
    locationIds: z.array(idSchema).max(200).optional(),
});
export const passwordChangeSchema = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: passwordSchema,
});
/* ------------------------------------------------------- tenants, locations */
export const tenantCreateSchema = z.object({
    name: z.string().trim().min(1).max(160),
    slug: slugSchema,
    contactEmail: z.string().trim().email().max(254).optional(),
});
export const tenantUpdateSchema = z.object({
    name: z.string().trim().min(1).max(160).optional(),
    contactEmail: z.string().trim().email().max(254).nullable().optional(),
    imprint: z.string().trim().max(4000).nullable().optional(),
    privacyNotice: z.string().trim().max(20000).nullable().optional(),
    defaultLanguage: languageSchema.optional(),
    languages: z.array(languageSchema).min(1).max(10).optional(),
    retentionDays: z
        .union([z.literal(7), z.literal(14), z.literal(30), z.literal(90), z.literal(180), z.literal(365)])
        .optional(),
    branding: brandingSchema.partial().optional(),
    features: z.record(z.enum(FEATURE_FLAGS), z.boolean()).optional(),
    fallbackTargets: routingTargetsSchema.partial().optional(),
});
export const locationUpsertSchema = z.object({
    name: z.string().trim().min(1).max(160),
    address: z.string().trim().max(400).nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    email: z.string().trim().email().max(254).nullable().default(null),
    timezone: z.string().min(1).max(64).default('Europe/Berlin'),
    openingHours: openingHoursSchema.optional(),
    closedBehaviour: z.enum(CLOSED_BEHAVIOURS).default('allow'),
    closedMessage: z.string().trim().max(1000).nullable().default(null),
    idleTimeoutSeconds: z.number().int().min(15).max(600).default(60),
    successScreenSeconds: z.number().int().min(2).max(60).default(8),
});
/* ------------------------------------------------------------ visitor types */
export const visitorTypeUpsertSchema = z.object({
    key: slugSchema,
    name: z.string().trim().min(1).max(80),
    nameI18n: z.record(languageSchema, z.string().max(80)).optional(),
    description: z.string().trim().max(200).nullable().default(null),
    descriptionI18n: z.record(languageSchema, z.string().max(200)).optional(),
    icon: z.string().trim().max(40).default('user'),
    active: z.boolean().default(true),
    order: z.number().int().min(0).max(99).default(0),
    reasons: z.array(fieldOptionSchema).max(50).default([]),
    contactTargets: z.array(fieldOptionSchema).max(200).default([]),
    askAppointment: z.boolean().default(false),
});
/* ------------------------------------------------------------- integrations */
export const pushConfigSchema = z.object({
    enabled: z.boolean().default(false),
    /**
     * How long the push service keeps the message while the device is offline.
     * A visitor standing at the counter is not interesting an hour later, so the
     * default is deliberately short.
     */
    ttlSeconds: z.number().int().min(0).max(86400).default(900),
    /** `high` wakes the device immediately; `normal` may be batched. */
    urgency: z.enum(['very-low', 'low', 'normal', 'high']).default('high'),
    /** Also notify users who are not scoped to the check-in's location. */
    ignoreLocationScope: z.boolean().default(false),
});
export const emailConfigSchema = z.object({
    enabled: z.boolean().default(false),
    host: z.string().trim().max(200).optional(),
    port: z.number().int().min(1).max(65535).default(587),
    secure: z.boolean().default(false),
    username: z.string().trim().max(200).optional(),
    password: z.string().max(400).optional(),
    from: z.string().trim().max(254).optional(),
    timeoutMs: z.number().int().min(1000).max(60000).default(15000),
});
export const webhookEndpointSchema = z.object({
    /** Referenced from routing rules - an admin can never enter a raw URL there. */
    key: slugSchema,
    /** Human readable name shown in the admin UI. Defaults to the key. */
    label: z.string().trim().max(120).optional(),
    url: z.string().trim().url().max(500),
    /** HMAC-SHA256 key; the receiver verifies `x-foyer-signature`. */
    secret: z.string().max(400).optional(),
    headers: z.record(z.string().max(80), z.string().max(400)).optional(),
    active: z.boolean().default(true),
});
export const webhookConfigSchema = z.object({
    enabled: z.boolean().default(false),
    endpoints: z.array(webhookEndpointSchema).max(20).default([]),
    timeoutMs: z.number().int().min(1000).max(60000).default(10000),
});
export const teamsConfigSchema = z.object({
    enabled: z.boolean().default(false),
    /** Microsoft Teams incoming webhook URL (per channel). */
    webhookUrl: z.string().trim().url().max(1000).optional(),
    timeoutMs: z.number().int().min(1000).max(60000).default(10000),
});
export const integrationUpdateSchema = z.object({
    email: emailConfigSchema.partial().optional(),
    push: pushConfigSchema.partial().optional(),
    webhook: webhookConfigSchema.partial().optional(),
    teams: teamsConfigSchema.partial().optional(),
});
/* ------------------------------------------------------- push subscriptions */
/**
 * A browser's `PushSubscription.toJSON()`. The endpoint is issued by the push
 * service of the browser vendor (FCM for Chrome/Edge, Mozilla autopush,
 * Apple), the keys are the client's public ECDH key and auth secret used to
 * encrypt the payload (RFC 8291).
 */
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().trim().url().max(1000),
    expirationTime: z.number().nullable().optional(),
    keys: z.object({
        p256dh: z.string().trim().min(1).max(200),
        auth: z.string().trim().min(1).max(100),
    }),
    /** Free text shown in the device list, e.g. "Windows - Edge". */
    label: z.string().trim().max(120).optional(),
});
/* ------------------------------------------------------------------ queries */
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).max(10000).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export const visitorQuerySchema = paginationSchema.extend({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    locationId: idSchema.optional(),
    deviceId: idSchema.optional(),
    visitorTypeKey: slugSchema.optional(),
    status: z.enum(VISITOR_STATUSES).optional(),
    /** The waiting area: waiting and seen, everything not finished yet. */
    open: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .optional(),
    search: z.string().trim().max(120).optional(),
    sort: z.enum(['createdAt', '-createdAt']).default('-createdAt'),
});
export const analyticsQuerySchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    locationId: idSchema.optional(),
    granularity: z.enum(['day', 'week', 'month']).default('day'),
});
export const auditQuerySchema = paginationSchema.extend({
    action: z.string().trim().max(80).optional(),
    actorId: idSchema.optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
/* --------------------------------------------------------------- setup DTOs */
export const setupSchema = z.object({
    admin: z.object({
        email: z.string().trim().email().max(254),
        name: z.string().trim().min(1).max(120),
        password: passwordSchema,
    }),
    tenant: tenantCreateSchema,
    location: locationUpsertSchema,
});
//# sourceMappingURL=schemas.js.map